import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../lib/AuthProvider';
import { useScreen } from '../lib/useScreen';
import { obtenerMensajes, enviarMensaje, marcarLeidos } from '../lib/mensajes';

const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";
const C = { turquesa:'#5dc1b9', mango:'#FDBE02' };

export default function ChatPage() {
  const { conexionId } = useParams();
  const { user } = useAuth();
  const { isMobile } = useScreen();
  const navigate = useNavigate();
  const [mensajes, setMensajes] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [otro, setOtro] = useState(null);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  // Load conexion info + messages
  useEffect(() => {
    if (!conexionId || !user?.id) return;
    const cargar = async () => {
      const { data: con } = await supabase.from('conexiones')
        .select('*, from:profiles!conexiones_user_from_id_fkey(id, full_name, business_name, avatar_url, business_type), to:profiles!conexiones_user_to_id_fkey(id, full_name, business_name, avatar_url, business_type)')
        .eq('id', conexionId).single();

      if (!con || con.estado !== 'aceptada') { navigate('/conexiones'); return; }
      setOtro(con.user_from_id === user.id ? con.to : con.from);

      const { data } = await obtenerMensajes(conexionId);
      setMensajes(data);
      setLoading(false);
      marcarLeidos(conexionId, user.id);
    };
    cargar();
  }, [conexionId, user?.id, navigate]);

  // Realtime subscription
  useEffect(() => {
    if (!conexionId) return;
    const channel = supabase.channel('chat-' + conexionId)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'mensajes', filter: `conexion_id=eq.${conexionId}` },
        (payload) => {
          setMensajes(prev => [...prev, payload.new]);
          if (payload.new.user_from_id !== user?.id) marcarLeidos(conexionId, user?.id);
        }
      ).subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [conexionId, user?.id]);

  // Scroll to bottom
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [mensajes.length]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    await enviarMensaje(conexionId, input);
    setInput('');
    setSending(false);
  };

  const nombre = otro?.business_name || otro?.full_name || 'Emprendedor';

  return (
    <div style={{ background:'#0a0a0a', minHeight:'100vh', fontFamily:font, color:'#fff', display:'flex', flexDirection:'column' }}>
      {/* Header */}
      <header style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 16px', borderBottom:'0.5px solid rgba(255,255,255,0.06)', flexShrink:0 }}>
        <button onClick={() => navigate('/conexiones')} style={{ background:'none', border:'none', color:C.turquesa, fontSize:14, cursor:'pointer', fontFamily:font, padding:0 }}>← Volver</button>
        <div style={{ width:1, height:20, background:'rgba(255,255,255,0.1)' }} />
        {otro?.avatar_url ? (
          <img src={otro.avatar_url} alt="" style={{ width:32, height:32, borderRadius:'50%', objectFit:'cover' }} />
        ) : (
          <div style={{ width:32, height:32, borderRadius:'50%', background:'linear-gradient(135deg, #5dc1b9, #FDBE02)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:600 }}>{nombre[0]}</div>
        )}
        <div>
          <p style={{ margin:0, fontSize:14, fontWeight:500 }}>{nombre}</p>
          <p style={{ margin:0, fontSize:10, color:'rgba(255,255,255,0.4)' }}>{otro?.business_type || ''}</p>
        </div>
      </header>

      {/* Messages */}
      <div style={{ flex:1, overflowY:'auto', padding:isMobile?'12px':'16px 24px' }}>
        {loading ? (
          <div style={{ textAlign:'center', padding:'60px 0' }}>
            <div style={{ width:24, height:24, border:'3px solid rgba(255,255,255,0.08)', borderTopColor:C.turquesa, borderRadius:'50%', animation:'tdSpin 0.8s linear infinite', margin:'0 auto' }} />
            <style>{`@keyframes tdSpin{to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : mensajes.length === 0 ? (
          <div style={{ textAlign:'center', padding:'60px 20px' }}>
            <p style={{ fontSize:32, margin:'0 0 8px' }}>👋</p>
            <p style={{ fontSize:14, color:'rgba(255,255,255,0.5)' }}>Inicia la conversacion con {nombre.split(' ')[0]}</p>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {mensajes.map(m => {
              const esMio = m.user_from_id === user?.id;
              return (
                <div key={m.id} style={{ display:'flex', justifyContent: esMio ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth:'75%',
                    padding:'10px 14px',
                    borderRadius: esMio ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    background: esMio ? 'rgba(93,193,185,0.15)' : 'rgba(255,255,255,0.06)',
                    border: `0.5px solid ${esMio ? 'rgba(93,193,185,0.25)' : 'rgba(255,255,255,0.1)'}`,
                  }}>
                    <p style={{ margin:0, fontSize:14, color:'rgba(255,255,255,0.9)', lineHeight:1.6 }}>{m.contenido}</p>
                    <p style={{ margin:'4px 0 0', fontSize:9, color:'rgba(255,255,255,0.3)', textAlign:'right' }}>
                      {new Date(m.created_at).toLocaleTimeString('es', { hour:'2-digit', minute:'2-digit' })}
                      {esMio && m.leido && ' ✓✓'}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <div style={{ padding:isMobile?'10px 12px':'12px 24px', borderTop:'0.5px solid rgba(255,255,255,0.06)', display:'flex', gap:8, flexShrink:0 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          placeholder="Escribe un mensaje..."
          style={{
            flex:1, padding:'12px 16px', background:'rgba(255,255,255,0.05)',
            border:'0.5px solid rgba(255,255,255,0.1)', borderRadius:50,
            color:'#fff', fontSize:14, fontFamily:font, outline:'none',
          }}
        />
        <button onClick={handleSend} disabled={!input.trim() || sending} style={{
          padding:'12px 20px', background: input.trim() ? C.turquesa : 'rgba(255,255,255,0.06)',
          border:'none', borderRadius:50, color:'#fff', fontSize:14, fontWeight:500,
          cursor: input.trim() ? 'pointer' : 'default', fontFamily:font, opacity: sending ? 0.5 : 1,
        }}>
          {sending ? '...' : '→'}
        </button>
      </div>
    </div>
  );
}
